import { FilterOperations as FO, TimeScales } from 'business/definitions';
import { isDate } from 'helpers/helpers';
import type { SentenceElement } from 'modules/decision/pages/Explore/types';

import {
    addLeafAtPath,
    changeLeafAtPath,
    mapTree,
    removeNodeAtPath,
    setConnectorAtPath,
    splitGroupAtPath,
    transformLeafToGroup,
} from '../../../helpers/filterTree';
import { getUpdatedFilterValue } from '../helpers';
import { getDisplayIndex } from './displayIndex';
import {
    dummyMetric,
    getDummyDimension,
    getDummyFilter,
    initialSentenceState,
} from './dummyElement';

/* Create a new metric */
export const addSentenceMetric = (
    state: SentenceQueryT,
    payload?: { field: string; aggregation: Aggregation },
): SentenceQueryT => {
    const displayIndex = state.metrics.length;
    const newMetric = {
        ...dummyMetric,
        displayIndex,
        field: payload?.field ?? dummyMetric.field,
        aggregation: payload?.aggregation ?? dummyMetric.aggregation,
    };
    const metrics = [...state.metrics, newMetric];
    return {
        ...state,
        metrics,
        dimensions: state.dimensions.map((dim) => ({
            ...dim,
            displayIndex: dim.displayIndex + 1,
        })),
    };
};

/* Create a new dimension */
export const addSentenceDimension = (
    state: SentenceQueryT,
    dimension?: AvailableDimensionT,
): SentenceQueryT => {
    const displayIndex = getDisplayIndex(state);
    const isFirst = state.dimensions.length === 0;
    const isTree = Boolean(dimension?.isTree);
    const newDimension: SentenceDimensionT = dimension
        ? {
              ...dimension,
              displayIndex,
              isFirst,
              aggregationStrategy: isTree ? 'flat' : null,
          }
        : getDummyDimension(displayIndex, isFirst);
    if (isDate(newDimension.type) && !newDimension.scale) {
        newDimension.scale = TimeScales.month;
    }
    return {
        ...state,
        dimensions: [...state.dimensions, newDimension],
    };
};

/* Append a new filter to the group at `path` (root by default), optionally pre-filled with a field. */
export const addSentenceFilter = (
    state: SentenceQueryT,
    path: number[] = [],
    filter?: AvailableElementT,
): SentenceQueryT => {
    const leaf: SentenceFilterT = filter
        ? { ...filter, operation: 'equal', value: '', displayIndex: 0 }
        : getDummyFilter();
    return { ...state, filters: addLeafAtPath(state.filters, path, leaf) };
};

/* Set the connector (and/or) of the group level at `path`. */
export const changeSentenceFiltersConnector = (
    state: SentenceQueryT,
    path: number[],
    connector: FilterConnectorT,
): SentenceQueryT => ({
    ...state,
    filters: setConnectorAtPath(state.filters, path, connector),
});

/* Wrap the filter at `path` into a new group (logic inverse of its parent level). */
export const transformSentenceFilterToGroup = (
    state: SentenceQueryT,
    path: number[],
): SentenceQueryT => ({
    ...state,
    filters: transformLeafToGroup(state.filters, path),
});

/* Dissolve the group at `path`, raising its children to the parent level. */
export const splitSentenceFilterGroup = (
    state: SentenceQueryT,
    path: number[],
): SentenceQueryT => ({
    ...state,
    filters: splitGroupAtPath(state.filters, path),
});

/* Remove the filter at `path`, pruning the group if it becomes empty. */
export const removeSentenceFilter = (
    state: SentenceQueryT,
    path: number[],
): SentenceQueryT => ({
    ...state,
    filters: removeNodeAtPath(state.filters, path),
});

/* Update an existing metric */
export const changeSentenceMetric = (
    state: SentenceQueryT,
    newMetric: SentenceMetricT,
): SentenceQueryT => {
    const { displayIndex } = newMetric;
    const index = state.metrics.findIndex(
        (d) => d.displayIndex === displayIndex,
    );
    if (index < 0) {
        return state;
    }
    const metric = {
        ...state.metrics[index],
        ...newMetric,
    };
    const newMetrics = [...state.metrics];
    newMetrics[index] = metric;
    return { ...state, metrics: newMetrics };
};

/* Update an existing dimension */
export const changeSentenceDimension = (
    state: SentenceQueryT,
    newDimension: SentenceDimensionT,
): SentenceQueryT => {
    const { displayIndex } = newDimension;
    const index = state.dimensions.findIndex(
        (d) => d.displayIndex === displayIndex,
    );
    if (index < 0) {
        return state;
    }
    const dimension = {
        ...state.dimensions[index],
        ...newDimension,
    };
    if (isDate(dimension.type) && !dimension.scale) {
        dimension.scale = TimeScales.month;
    } else if (!isDate(dimension.type) && dimension.scale) {
        delete dimension.scale;
    }
    const newDimensions = [...state.dimensions];
    newDimensions[index] = dimension;
    return { ...state, dimensions: newDimensions };
};

/* Update the filter at `path`, keeping a compatible value across field changes. */
export const changeSentenceFilter = (
    state: SentenceQueryT,
    path: number[],
    newFilter: SentenceFilterT,
): SentenceQueryT => ({
    ...state,
    filters: changeLeafAtPath(state.filters, path, (old) => ({
        ...newFilter,
        value: getUpdatedFilterValue(old, newFilter),
    })),
});

export const resetSentence = (): SentenceQueryT => initialSentenceState;

const updateElementDisplayIndex = <T extends SentenceElement>(
    element: T,
    index: number,
): T => {
    const { displayIndex } = element;
    return {
        ...element,
        displayIndex: displayIndex > index ? displayIndex - 1 : displayIndex,
    };
};

const removeSentenceElement = <T extends SentenceElement>(
    elements: T[],
    index: number,
): T[] =>
    elements
        .filter((e) => e.displayIndex !== index)
        .map((e) => updateElementDisplayIndex(e, index));

// Removes a metric or dimension by its global displayIndex. Filters are removed by path
// (removeSentenceFilter), so they are left untouched here.
export const removeQueryItem = (
    state: SentenceQueryT,
    index: number,
): SentenceQueryT => ({
    ...state,
    metrics: removeSentenceElement(state.metrics, index),
    dimensions: removeSentenceElement(state.dimensions, index),
});

export const setSentenceQuery = (
    state: SentenceQueryT,
    query: SentenceQueryT,
): SentenceQueryT => {
    const selectedMetrics = query.metrics.map((m, index) => ({
        ...m,
        displayIndex: index,
    }));

    // We sort dimensions so that even if the displayIndex is reset, the order
    // is maintained. It is important for non-smart frontend queries.
    const selectedDimensions = query.dimensions
        .sort((a, b) => {
            if (a.displayIndex && b.displayIndex) {
                return a.displayIndex - b.displayIndex;
            }
            return 0;
        })
        .map((d, index) => ({
            ...d,
            displayIndex: index + selectedMetrics.length,
        }));

    // Preserve the filter tree structure; only apply the `is_null`/`is_not_null` transformation
    // per leaf. displayIndex is derived at render.
    const selectedFilters = mapTree(query.filters, (f) =>
        f.value === null &&
        (f.operation === FO.equal || f.operation === FO.not_equal)
            ? {
                  ...f,
                  operation:
                      f.operation === FO.equal ? FO.is_null : FO.is_not_null,
              }
            : f,
    );

    return {
        ...state,
        metrics: selectedMetrics,
        dimensions: selectedDimensions,
        filters: selectedFilters,
    };
};
